import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  RadarChart, 
  Radar, 
  PolarGrid, 
  PolarAngleAxis, 
  PolarRadiusAxis,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format } from "date-fns";

// Radar chart for Fruits of Spirit
export const FruitsRadarChart = ({ data }) => {
  if (!data) return null;
  
  const chartData = [
    { fruit: 'Love', value: data.love || 0 },
    { fruit: 'Joy', value: data.joy || 0 },
    { fruit: 'Peace', value: data.peace || 0 },
    { fruit: 'Patience', value: data.patience || 0 },
    { fruit: 'Kindness', value: data.kindness || 0 },
    { fruit: 'Goodness', value: data.goodness || 0 },
    { fruit: 'Faithfulness', value: data.faithfulness || 0 },
    { fruit: 'Gentleness', value: data.gentleness || 0 },
    { fruit: 'Self-Control', value: data.self_control || 0 },
  ];

  return (
    <ResponsiveContainer width="100%" height={300}>
      <RadarChart data={chartData}>
        <PolarGrid />
        <PolarAngleAxis dataKey="fruit" tick={{ fontSize: 11 }} />
        <PolarRadiusAxis domain={[0, 10]} tick={{ fontSize: 10 }} />
        <Radar 
          name="Score" 
          dataKey="value" 
          stroke="#f59e0b" 
          fill="#fbbf24" 
          fillOpacity={0.5} 
        />
        <Tooltip />
      </RadarChart>
    </ResponsiveContainer>
  );
};

// Bar chart for Glorifying God
export const GlorifyingBarChart = ({ data }) => {
  if (!data) return null;
  
  const chartData = [
    { name: 'Words', value: data.use_of_words || 0 },
    { name: 'Thoughts', value: data.thought_life || 0 },
    { name: 'Time', value: data.use_of_time || 0 },
    { name: 'Money', value: data.use_of_money || 0 },
    { name: 'Temptations', value: data.management_of_temptations || 0 },
  ];

  return (
    <ResponsiveContainer width="100%" height={250}>
      <BarChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
        <YAxis domain={[0, 10]} />
        <Tooltip />
        <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

// Line chart for progress over time
export const ProgressLineChart = ({ checkIns }) => {
  if (!checkIns || checkIns.length === 0) return null;
  
  const chartData = checkIns
    .slice()
    .reverse()
    .map(c => ({
      date: format(new Date(c.week_start), 'MMM d'),
      lovedGod: c.loved_well?.loved_god || 0,
      lovedPeople: c.loved_well?.loved_people || 0,
      fruitsAvg: c.fruits_of_spirit ? 
        (Object.values(c.fruits_of_spirit).reduce((a, b) => a + b, 0) / 9).toFixed(1) : 0,
      glorifyingAvg: c.glorifying_god ?
        (Object.values(c.glorifying_god).reduce((a, b) => a + b, 0) / 5).toFixed(1) : 0,
    }));

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
        <YAxis domain={[0, 10]} />
        <Tooltip />
        <Legend />
        <Line type="monotone" dataKey="lovedGod" name="Loved God" stroke="#ef4444" strokeWidth={2} />
        <Line type="monotone" dataKey="lovedPeople" name="Loved People" stroke="#ec4899" strokeWidth={2} />
        <Line type="monotone" dataKey="fruitsAvg" name="Fruits Avg" stroke="#f59e0b" strokeWidth={2} />
        <Line type="monotone" dataKey="glorifyingAvg" name="Glorifying Avg" stroke="#3b82f6" strokeWidth={2} />
      </LineChart>
    </ResponsiveContainer>
  );
};

// Class average charts for teachers
export const ClassAverageBarChart = ({ checkIns }) => {
  if (!checkIns || checkIns.length === 0) return null;
  
  // Calculate averages across all check-ins
  const totals = {
    lovedGod: 0,
    lovedPeople: 0,
    love: 0, joy: 0, peace: 0, patience: 0, kindness: 0,
    goodness: 0, faithfulness: 0, gentleness: 0, selfControl: 0,
    words: 0, thoughts: 0, time: 0, money: 0, temptations: 0
  };
  
  checkIns.forEach(c => {
    totals.lovedGod += c.loved_well?.loved_god || 0;
    totals.lovedPeople += c.loved_well?.loved_people || 0;
    totals.love += c.fruits_of_spirit?.love || 0;
    totals.joy += c.fruits_of_spirit?.joy || 0;
    totals.peace += c.fruits_of_spirit?.peace || 0;
    totals.patience += c.fruits_of_spirit?.patience || 0;
    totals.kindness += c.fruits_of_spirit?.kindness || 0;
    totals.goodness += c.fruits_of_spirit?.goodness || 0;
    totals.faithfulness += c.fruits_of_spirit?.faithfulness || 0;
    totals.gentleness += c.fruits_of_spirit?.gentleness || 0;
    totals.selfControl += c.fruits_of_spirit?.self_control || 0;
    totals.words += c.glorifying_god?.use_of_words || 0;
    totals.thoughts += c.glorifying_god?.thought_life || 0;
    totals.time += c.glorifying_god?.use_of_time || 0;
    totals.money += c.glorifying_god?.use_of_money || 0;
    totals.temptations += c.glorifying_god?.management_of_temptations || 0;
  });
  
  const count = checkIns.length;
  
  const loveData = [
    { name: 'Loved God', value: (totals.lovedGod / count).toFixed(1) },
    { name: 'Loved People', value: (totals.lovedPeople / count).toFixed(1) },
  ];
  
  const fruitsData = [
    { fruit: 'Love', value: (totals.love / count).toFixed(1) },
    { fruit: 'Joy', value: (totals.joy / count).toFixed(1) },
    { fruit: 'Peace', value: (totals.peace / count).toFixed(1) },
    { fruit: 'Patience', value: (totals.patience / count).toFixed(1) },
    { fruit: 'Kindness', value: (totals.kindness / count).toFixed(1) },
    { fruit: 'Goodness', value: (totals.goodness / count).toFixed(1) },
    { fruit: 'Faithfulness', value: (totals.faithfulness / count).toFixed(1) },
    { fruit: 'Gentleness', value: (totals.gentleness / count).toFixed(1) },
    { fruit: 'Self-Control', value: (totals.selfControl / count).toFixed(1) },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-sm font-medium text-slate-700 mb-2">Love Assessment Average</h4>
        <ResponsiveContainer width="100%" height={150}>
          <BarChart data={loveData} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" domain={[0, 10]} />
            <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="value" fill="#ef4444" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      <div>
        <h4 className="text-sm font-medium text-slate-700 mb-2">Fruits of Spirit Average</h4>
        <ResponsiveContainer width="100%" height={300}>
          <RadarChart data={fruitsData}>
            <PolarGrid />
            <PolarAngleAxis dataKey="fruit" tick={{ fontSize: 10 }} />
            <PolarRadiusAxis domain={[0, 10]} tick={{ fontSize: 9 }} />
            <Radar name="Class Avg" dataKey="value" stroke="#f59e0b" fill="#fbbf24" fillOpacity={0.5} />
            <Tooltip />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// Full check-in detail display
export const CheckInFullDisplay = ({ checkIn }) => {
  if (!checkIn) return null;

  return (
    <Tabs defaultValue="overview" className="w-full">
      <TabsList className="grid w-full grid-cols-4">
        <TabsTrigger value="overview">Overview</TabsTrigger>
        <TabsTrigger value="fruits">Fruits</TabsTrigger>
        <TabsTrigger value="glorifying">Glorifying</TabsTrigger>
        <TabsTrigger value="reflection">Reflection</TabsTrigger>
      </TabsList>
      
      <TabsContent value="overview" className="space-y-4 mt-4">
        <div className="grid grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-red-600">Loved God</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold">{checkIn.loved_well?.loved_god}/10</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-pink-600">Loved People</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold">{checkIn.loved_well?.loved_people}/10</p>
            </CardContent>
          </Card>
        </div>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Averages</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-amber-50 rounded-lg">
                <p className="text-xs text-amber-700">Fruits of Spirit</p>
                <p className="text-2xl font-bold text-amber-600">
                  {checkIn.fruits_of_spirit ? 
                    (Object.values(checkIn.fruits_of_spirit).reduce((a, b) => a + b, 0) / 9).toFixed(1) : '-'}
                </p>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-700">Glorifying God</p>
                <p className="text-2xl font-bold text-blue-600">
                  {checkIn.glorifying_god ?
                    (Object.values(checkIn.glorifying_god).reduce((a, b) => a + b, 0) / 5).toFixed(1) : '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>
      
      <TabsContent value="fruits" className="mt-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Fruits of the Spirit</CardTitle>
          </CardHeader>
          <CardContent>
            <FruitsRadarChart data={checkIn.fruits_of_spirit} />
            <div className="grid grid-cols-3 gap-2 mt-4 text-sm">
              {checkIn.fruits_of_spirit && Object.entries(checkIn.fruits_of_spirit).map(([key, value]) => (
                <div key={key} className="flex justify-between p-2 bg-slate-50 rounded">
                  <span className="capitalize">{key.replace('_', '-')}</span>
                  <span className="font-bold">{value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </TabsContent>
      
      <TabsContent value="glorifying" className="mt-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Glorifying God</CardTitle>
          </CardHeader>
          <CardContent>
            <GlorifyingBarChart data={checkIn.glorifying_god} />
            <div className="grid grid-cols-1 gap-2 mt-4 text-sm">
              {checkIn.glorifying_god && [
                { key: 'use_of_words', label: 'Use of Words' },
                { key: 'thought_life', label: 'Thought Life' },
                { key: 'use_of_time', label: 'Use of Time' },
                { key: 'use_of_money', label: 'Use of Money' },
                { key: 'management_of_temptations', label: 'Management of Temptations' },
              ].map(({ key, label }) => (
                <div key={key} className="flex justify-between p-2 bg-slate-50 rounded">
                  <span>{label}</span>
                  <span className="font-bold">{checkIn.glorifying_god[key]}/10</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </TabsContent>
      
      <TabsContent value="reflection" className="mt-4 space-y-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600">Biggest Fear</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-800">{checkIn.reflection?.biggest_fear || 'No response'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600">Biggest Temptation</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-800">{checkIn.reflection?.biggest_temptation || 'No response'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600">Biggest Victory</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-800">{checkIn.reflection?.biggest_victory || 'No response'}</p>
          </CardContent>
        </Card>
        {!checkIn.was_honest && (
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-amber-700">Honesty Note</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-amber-800">{checkIn.dishonesty_reason || 'No explanation provided'}</p>
            </CardContent>
          </Card>
        )}
      </TabsContent>
    </Tabs>
  );
};

export default { 
  FruitsRadarChart, 
  GlorifyingBarChart, 
  ProgressLineChart, 
  ClassAverageBarChart,
  CheckInFullDisplay 
};
